import { computed, h, Ref, VNode, withDirectives } from "vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { DS_ICON_APPEARANCES, DS_ICON_SIZES } from "@/src/design_system/primitives/icon/types";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import { DS_OPTION_LABEL_CLASSES } from "@/src/design_system/form_elements/shared_modules/option_label/constants/classes";
import type { DSChoiceOption } from "@/src/design_system/form_elements/shared/types/option";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";

export const useRenderLabelContent = (option: Ref<DSChoiceOption>, tooltip: Ref<string | undefined>) => {
  const shouldWrapWithContent = computed(() => option.value.label && (option.value.icon || option.value.description));

  const getIconNode = (): VNode | null => {
    return option.value.icon
      ? h(DSIcon, {
          icon: option.value.icon as DSIconName,
          appearance: DS_ICON_APPEARANCES.Outlined,
          size: DS_ICON_SIZES.Small,
        })
      : null;
  };

  const getLabelTextNode = (): VNode | null => {
    if (!option.value.label) return null;

    const labelNode = h(
      "span",
      {
        class: DS_OPTION_LABEL_CLASSES.Label,
        "data-cy": "ds-option-label__label",
      },
      [option.value.label]
    );

    return withDirectives(labelNode, [[vDsTooltip, tooltip.value]]);
  };

  const getDescriptionTextNode = (): VNode | null => {
    return option.value.description
      ? h("span", { class: DS_OPTION_LABEL_CLASSES.Description }, [option.value.description])
      : null;
  };

  const getLabelWithDescriptionNode = (): VNode | null => {
    const labelText = getLabelTextNode();
    const descriptionText = getDescriptionTextNode();

    return labelText && descriptionText
      ? h("span", { class: DS_OPTION_LABEL_CLASSES.Text }, [labelText, descriptionText])
      : labelText;
  };

  const renderLabelContent = () => {
    const children = [];

    const iconNode = getIconNode();
    const labelNode = getLabelWithDescriptionNode();

    if (iconNode) children.push(iconNode);
    if (labelNode) children.push(labelNode);

    return shouldWrapWithContent.value ? h("span", { class: DS_OPTION_LABEL_CLASSES.Content }, children) : children;
  };

  return { renderLabelContent };
};
